import { useState, useEffect, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Error } from "../models/error";
import { JobType } from "../models/jobType";
import { Filters, Searches, Sort, Sorts } from "../models/shipeezi.table";
import { StorageBoxType } from "../models/storage.box.type";
import JobTypeService from "../services/job.type.service";
import StorageBoxTypeService from "../services/storage.box.type.service";
import { checkError } from "../util/checkError";
import { Status } from "../util/enums";
import { handleSort } from "../util/handleSort";

export const useStorageBoxTypesHook = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [storageBoxTypesData, setStorageBoxTypesData] = useState<StorageBoxType[]>([]);

  const [filters, setFilters] = useState<Filters>({ isActive: ["1"] });
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>();
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setHasError(undefined);
    }
  }, [hasError]);

  const attributes = useMemo(
    () => ({
      storageBoxes: ["id", "isActive", "valid", "singleUseJobId"],
    }),
    []
  );

  const getStorageBoxTypesList = () =>
    useQuery(
      ["storage.box.types.list", page, pageSize, searches, sorting, filters],
      () =>
        StorageBoxTypeService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
              : JSON.stringify([["updatedAt", "desc"]]),
          include: JSON.stringify([
            { association: "storageBoxes", attributes: attributes.storageBoxes },
            { association: "storageBoxTypeSealRules" },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setStorageBoxTypesData(response.data);
        },
      }
    );

  const getStorageBoxTypesCount = () =>
    useQuery(
      ["storage.box.types.count", searches, filters],
      () =>
        StorageBoxTypeService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];

  const { refetch: refetchStorageBoxTypes } = getStorageBoxTypesList();

  const useCreateStorageBoxType = () => {
    return useMutation(
      ["storage.box.type.create"],
      (storageBoxType: StorageBoxType) =>
        StorageBoxTypeService.create({ ...storageBoxType, companyId: currentUser?.companyId }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["storage.box.type.list.in.warehouse"] });

          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setHasError(false);
          refetchStorageBoxTypes();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const useUpdateStorageBoxType = () => {
    return useMutation(
      ["storage.box.type.update"],
      ({ id, storageBoxType }: { id: number; storageBoxType: StorageBoxType }) =>
        StorageBoxTypeService.update(id, storageBoxType),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["storage.box.type.list.in.warehouse"] });

          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.success.storage.box.type",
          });
          setHasError(false);
          refetchStorageBoxTypes();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const { isFetching: isFetchingJobTypes } = useQuery(
    ["job.type.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setJobTypes(response.data);
      },
    }
  );

  return {
    page,
    total,
    pageSize,
    hasError,
    setPageSize,
    handleSearch,
    handleSorting,
    handleFilter,
    storageBoxTypesData,
    handleCurrentPage,
    handleGetDefaultFilters,

    getStorageBoxTypesList,
    useUpdateStorageBoxType,
    useCreateStorageBoxType,
    getStorageBoxTypesCount,

    jobTypes,
    isFetchingJobTypes,
  };
};
